import React from 'react';
import styled from 'styled-components';

import {
  MenuItemsInsight,
  OfflineTimeInsight,
  PerformanceInsight,
  PreparationInsight,
  RejectionInsight,
  TotalRejectedOrdersInsight,
  RatingsInsight,
  RefundsOverviewInsight,
} from 'apis/merchant-gateway/insights';
import { BREAKPOINT_MOBILE, BREAKPOINT_TABLET } from 'modules/insights-v2/constants';

import CustomerBase from './CustomerBase';
import MenuItems from './MenuItems';
import Operations from './Operations';
import RatingsAndReviews from './RatingsAndReviews';
import { InsightEntity, InsightResponsePaginated, InsightResponseWithPreviousInterval } from '../../types';

type InsightSummaryProps = {
  insightEntity: InsightEntity;
  performanceInsight: InsightResponseWithPreviousInterval<PerformanceInsight>;
  ratingsInsight: InsightResponseWithPreviousInterval<RatingsInsight>;
  preparationInsight: InsightResponseWithPreviousInterval<PreparationInsight>;
  totalRejectedOrdersInsight: InsightResponseWithPreviousInterval<TotalRejectedOrdersInsight>;
  menuItemsInsight: InsightResponsePaginated<MenuItemsInsight>;
  rejectionInsight: InsightResponseWithPreviousInterval<RejectionInsight>;
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  refundsOverviewInsight: InsightResponseWithPreviousInterval<RefundsOverviewInsight>;
  isNewAlivenessEnabled: boolean;
  isRefundOverviewEnabled: boolean;
  isVATEnabled: boolean;
  hidePrepTime?: boolean;
  showHybridDeliveryDisclaimer?: boolean;
};

const Root = styled.div`
  align-items: flex-start;
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  padding: 24px;
  @media (max-width: ${BREAKPOINT_TABLET}px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    grid-template-columns: 1fr;
  }
`;

const InsightSummary = ({
  performanceInsight,
  ratingsInsight,
  preparationInsight,
  totalRejectedOrdersInsight,
  menuItemsInsight,
  rejectionInsight,
  offlineTimeInsight,
  refundsOverviewInsight,
  isNewAlivenessEnabled,
  isRefundOverviewEnabled,
  isVATEnabled,
  hidePrepTime,
  showHybridDeliveryDisclaimer,
}: InsightSummaryProps) => {
  return (
    <Root>
      <RatingsAndReviews ratingsInsight={ratingsInsight} />
      <Operations
        preparationInsight={preparationInsight}
        rejectedOrdersInsight={totalRejectedOrdersInsight}
        rejectionsInsight={rejectionInsight}
        offlineTimeInsight={offlineTimeInsight}
        refundsOverviewInsight={refundsOverviewInsight}
        isNewAlivenessEnabled={isNewAlivenessEnabled}
        isRefundOverviewEnabled={isRefundOverviewEnabled}
        isVATEnabled={isVATEnabled}
        hidePrepTime={hidePrepTime}
        showHybridDeliveryDisclaimer={showHybridDeliveryDisclaimer}
      />
      <CustomerBase performanceInsight={performanceInsight} />
      <MenuItems
        isError={menuItemsInsight.isError || (menuItemsInsight.isLoading && !menuItemsInsight.isFetching)}
        onRetry={menuItemsInsight.refetch}
        isFetching={menuItemsInsight.isFetching}
        menuItemsData={menuItemsInsight.data?.pages[0]}
      />
    </Root>
  );
};

export default InsightSummary;
